import { ResponsiveBar } from '@nivo/bar'
import { GLoading } from 'components/basic-blocks'
import { RiCheckboxBlankCircleFill } from 'react-icons/ri'
import { twMerge } from 'tailwind-merge'
import { BarChartProps } from './types'
import { BarItem } from '../../channel-details/bar-item'
import { resolveThemeColor, useThemeColors } from 'hooks'
import { useNivoTheme } from 'components/charts/nivo-theme'

const themeColorDefinitions = {
	// The tick labels are TEXT. #b8b9c4 in dark, which is the same ink the
	// sibling charts take from `--chart-axis-strong`.
	axisText: { variable: '--color-gray-500', fallback: '#6B7280' },
	// The axis LEGEND is the `axisLeftLabel` string. Only two callers pass one
	// (rules-analyze.tsx:656 and recovery-timeline.tsx:52), and the old theme
	// dropped the key, so nivo painted that label with its hardcoded #333 on
	// the dark card. `--chart-axis-strong` holds that exact #333 in light.
	axisLegend: { variable: '--chart-axis-strong', fallback: '#333' },
	// The grid is a RULE, not text. It shared the tick-label entry, which was
	// the last one-entry-two-jobs binding left in the repo. Both still resolve
	// to the same variable today, so no pixels move. The split stops a re-tune
	// of the label ink from dragging the grid with it.
	grid: { variable: '--color-gray-500', fallback: '#6B7280' },
	defaultBar: { variable: '--t-bar-pink', fallback: '#FFAFF8' }
} as const

export const BarChart = ({
	data,
	index,
	keys,
	layout = 'vertical',
	stacked = false,
	reversed = false,
	leftPadding = 10,
	tooltip,
	containerClassName,
	barPadding = 0.5,
	hideAxisLeft = true,
	axisLeftLabel,
	axisBottomFormat,
	circleFill = true,
	borderRadius = 0,
	roundTopOnly = false,
	marginBottom
}: BarChartProps) => {
	const themeColors = useThemeColors(themeColorDefinitions)
	const nivoTheme = useNivoTheme()
	if (!data) return <GLoading />

	const vertical = layout === 'vertical'

	// Spread the shared axis object, do not replace it. A plain replacement
	// drops every key the shared theme sets, and `axis.legend.text.fill` is one
	// of them.
	const theme = {
		...nivoTheme,
		axis: {
			...nivoTheme.axis,
			ticks: {
				...nivoTheme.axis.ticks,
				text: {
					fill: themeColors.axisText,
					fontSize: 10
				}
			},
			legend: {
				text: {
					fill: themeColors.axisLegend
				}
			}
		},
		grid: {
			line: {
				stroke: themeColors.grid,
				strokeWidth: 0.5,
				strokeOpacity: 0.2
			}
		}
	}

	return (
		<div
			className={twMerge(
				vertical ? 'h-[200px]' : 'h-[300px]',
				containerClassName
			)}
		>
			<ResponsiveBar
				data={data}
				margin={{
					top: vertical && axisLeftLabel ? 10 : 0,
					right: 10,
					bottom: vertical ? marginBottom ?? 20 : -10,
					left: hideAxisLeft ? leftPadding : 50
				}}
				indexBy={index}
				layout={layout}
				keys={keys}
				colors={({ data, id }): string => {
					return (
						resolveThemeColor(
							(data[`color_${id}`] as string) ||
								(data['color'] as string) ||
								themeColors.defaultBar
						)
					)
				}}
				barComponent={
					borderRadius > 0 && stacked
						? (props) => (
								<BarItem
									{...props}
									clip={{
										x: 0,
										y: 0,
										radius: borderRadius,
										radiusEnabled: {
											top: true,
											bottom: false,
											left: true,
											right: true
										},
										stacked: true,
										stackedTopKey: keys[keys.length - 1]
									}}
								/>
						  )
						: borderRadius > 0
						? (props) => (
								<BarItem
									{...props}
									clip={{
										x: 0,
										y: 0,
										radius: borderRadius,
										// roundTopOnly: both top corners rounded, square base
										// (needs left+right enabled so neither top corner is
										// pushed outside the bar). Default keeps prior behavior.
										radiusEnabled: roundTopOnly
											? {
													top: true,
													bottom: false,
													left: true,
													right: true
											  }
											: {
													top: true,
													bottom: true,
													left: false,
													right: false
											  }
									}}
								/>
						  )
						: undefined
				}
				colorBy="id"
				reverse={reversed}
				groupMode={stacked ? 'stacked' : 'grouped'}
				enableLabel={false}
				padding={barPadding}
				theme={theme}
				axisLeft={
					hideAxisLeft
						? null
						: {
								tickSize: 5,
								tickPadding: 5,
								tickRotation: 0,
								legend: axisLeftLabel || '',
								legendPosition: 'middle',
								legendOffset: axisLeftLabel ? -40 : 0,
								renderTick: (tick) => {
									return (
										<g transform={`translate(${tick.x},${tick.y})`}>
											<text
												x={-5}
												y={0}
												textAnchor="end"
												dominantBaseline="middle"
												fontSize={10}
												fill={themeColors.axisText}
											>
												{tick.value}
											</text>
										</g>
									)
								}
						  }
				}
				axisBottom={vertical ? { format: axisBottomFormat } : null}
				enableGridX={!vertical}
				enableGridY={vertical}
				tooltip={({ id, data, color }) => (
					<div
						data-theme="light"
						className="border rounded bg-white shadow-sm p-1 flex space-x-1 items-center text-xs text-t-desc-gray"
					>
						{circleFill && (
							<RiCheckboxBlankCircleFill
								className="w-5 h-5"
								style={{ color: color }}
							/>
						)}
						{tooltip(data, id)}
					</div>
				)}
			/>
		</div>
	)
}
